import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip } from 'primereact/tooltip';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import './SyncroSidebar.scss';
import { impersonationActive, stopImpersonation } from './service/impersonation';
import { toast } from 'react-toastify';

const getStoredUser = () => {
    try {
        const payload = JSON.parse(localStorage.getItem('userDetails') || localStorage.getItem('adminUser') || '{}');
        return payload?.data?.adminUser || {};
    } catch (error) {
        return {};
    }
};

const isRouteActive = (pathname, route, exact = false) => {
    if (!route) return false;
    if (route === '/' || exact) return pathname === route;
    return pathname === route || pathname.startsWith(`${route}/`);
};

const getItemActive = (item, pathname) =>
    item.children
        ? item.children.some((child) => isRouteActive(pathname, child.route, child.exact))
        : isRouteActive(pathname, item.route, item.exact);

const SidebarItem = ({ item, collapsed, onNavigate, pathname }) => {
    const isActive = getItemActive(item, pathname);
    const [isOpen, setIsOpen] = useState(isActive);
    const tooltipProps = collapsed
        ? {
              'data-pr-tooltip': item.label,
              'data-pr-position': 'right',
              title: item.label
          }
        : {};

    useEffect(() => {
        if (isActive) setIsOpen(true);
    }, [isActive]);

    if (item.children) {
        return (
            <div className={`syncro-sidebar__group${isActive ? ' syncro-sidebar__group--active' : ''}`}>
                <button
                    type="button"
                    className="syncro-sidebar__item syncro-sidebar__group-toggle"
                    aria-label={item.label}
                    aria-expanded={isOpen}
                    onClick={() => setIsOpen((current) => !current)}
                    {...tooltipProps}
                >
                    <i className={`syncro-sidebar__icon ${item.icon}`} aria-hidden="true"></i>
                    <span className="syncro-sidebar__item-label">{item.label}</span>
                    <i className={`syncro-sidebar__chevron pi ${isOpen ? 'pi-chevron-up' : 'pi-chevron-down'}`} aria-hidden="true"></i>
                </button>

                {isOpen && (
                    <div className="syncro-sidebar__children">
                        {item.children.map((child) => (
                            <SidebarItem key={child.id} item={child} collapsed={collapsed} onNavigate={onNavigate} pathname={pathname} />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <NavLink
            to={item.route}
            end={item.route === '/' || item.exact}
            className={({ isActive: navLinkActive }) =>
                `syncro-sidebar__item${navLinkActive || isActive ? ' syncro-sidebar__item--active' : ''}`
            }
            aria-label={item.label}
            onClick={onNavigate}
            {...tooltipProps}
        >
            <i className={`syncro-sidebar__icon ${item.icon}`} aria-hidden="true"></i>
            <span className="syncro-sidebar__item-label">{item.label}</span>
        </NavLink>
    );
};

const SyncroSidebar = ({ sections, collapsed, isMobileViewport, mobileOpen, onInteraction, onToggleCollapsed, onCloseMobile, translate }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const mobileCloseRef = useRef(null);
    const user = useMemo(getStoredUser, []);
    const firstName = user?.firstName || user?.firstname || '';
    const lastName = user?.lastName || user?.lastname || '';
    const displayName = `${firstName} ${lastName}`.trim() || user?.name || user?.email || translate('My Profile');
    const roleLabel = user?.role?.title || user?.role?.name || user?.role?.uid || '';
    const initials =
        [firstName, lastName]
            .filter(Boolean)
            .map((value) => String(value).charAt(0).toUpperCase())
            .join('')
            .slice(0, 2) || 'S';

    const handleLogout = async () => {
        if (impersonationActive()) {
            try { await stopImpersonation({ logout: true }); }
            catch (error) { toast.error('No pudimos cerrar la impersonación. Intenta nuevamente.'); }
            return;
        }
        window.localStorage.clear();
        navigate('/login');
    };

    useEffect(() => {
        if (mobileOpen) mobileCloseRef.current?.focus();
    }, [mobileOpen]);

    return (
        <>
            {collapsed && <Tooltip target=".syncro-sidebar [data-pr-tooltip]" position="right" showDelay={150} />}

            <button
                type="button"
                className={`syncro-sidebar-backdrop${mobileOpen ? ' syncro-sidebar-backdrop--visible' : ''}`}
                aria-label={translate('Close navigation')}
                tabIndex={mobileOpen ? 0 : -1}
                onClick={onCloseMobile}
            />

            <aside
                id="syncro-navigation"
                className={`syncro-sidebar${collapsed ? ' syncro-sidebar--collapsed' : ''}${mobileOpen ? ' syncro-sidebar--mobile-open' : ''}`}
                aria-label={translate('Main navigation')}
                aria-hidden={isMobileViewport && !mobileOpen}
                onClick={onInteraction}
            >
                <div className="syncro-sidebar__header">
                    <button type="button" className="syncro-sidebar__brand p-link" aria-label={translate('Dashboard')} onClick={() => navigate('/')}>
                        <img src="assets/layout/images/SYNCRO.png" alt="Syncro" />
                    </button>

                    <button
                        type="button"
                        className="syncro-sidebar__collapse-button p-link"
                        aria-label={collapsed ? translate('Expand navigation') : translate('Collapse navigation')}
                        title={collapsed ? translate('Expand navigation') : translate('Collapse navigation')}
                        onClick={onToggleCollapsed}
                    >
                        <i className={`pi ${collapsed ? 'pi-angle-right' : 'pi-angle-left'}`} aria-hidden="true"></i>
                    </button>

                    <button
                        type="button"
                        className="syncro-sidebar__mobile-close p-link"
                        aria-label={translate('Close navigation')}
                        ref={mobileCloseRef}
                        onClick={onCloseMobile}
                    >
                        <i className="pi pi-times" aria-hidden="true"></i>
                    </button>
                </div>

                <nav className="syncro-sidebar__navigation" aria-label={translate('Main navigation')}>
                    {sections.map((section) => (
                        <section className="syncro-sidebar__section" key={section.id} aria-labelledby={`syncro-section-${section.id}`}>
                            <h2 id={`syncro-section-${section.id}`} className="syncro-sidebar__section-title">
                                {section.label}
                            </h2>
                            <div className="syncro-sidebar__section-items">
                                {section.items.map((item) => (
                                    <SidebarItem
                                        key={item.id}
                                        item={item}
                                        collapsed={collapsed}
                                        pathname={location.pathname}
                                        onNavigate={onCloseMobile}
                                    />
                                ))}
                            </div>
                        </section>
                    ))}
                </nav>

                <div className="syncro-sidebar__profile">
                    <button
                        type="button"
                        className="syncro-sidebar__profile-main p-link"
                        aria-label={translate('My Profile')}
                        data-pr-tooltip={collapsed ? translate('My Profile') : undefined}
                        data-pr-position="right"
                        title={collapsed ? translate('My Profile') : undefined}
                        onClick={() => {
                            navigate('/profile');
                            onCloseMobile();
                        }}
                    >
                        <span className="syncro-sidebar__avatar" aria-hidden="true">
                            {initials}
                        </span>
                        <span className="syncro-sidebar__profile-copy">
                            <strong>{displayName}</strong>
                            {roleLabel && <small>{roleLabel}</small>}
                        </span>
                    </button>
                    <button
                        type="button"
                        className="syncro-sidebar__logout p-link"
                        aria-label={translate('Logout')}
                        data-pr-tooltip={collapsed ? translate('Logout') : undefined}
                        data-pr-position="right"
                        title={collapsed ? translate('Logout') : undefined}
                        onClick={handleLogout}
                    >
                        <i className="pi pi-sign-out" aria-hidden="true"></i>
                    </button>
                </div>
            </aside>
        </>
    );
};

export default SyncroSidebar;
